/**
 * How a file reaches a model: the size a picture is kept at, the shape a read
 * travels in, and how much of the history carries one.
 *
 * Bytes are shuttled once. Whoever makes a picture (a page render, a sketch
 * screenshot, an image too big to read as it stands) stores it already
 * model-readable and hands back a {@link ModelFile}: a durable reference,
 * never bytes and never a signed URL. A tool result carries the reference, the
 * transcript renders it, and {@link readToModelOutput} resolves it per prompt
 * build into something the provider can fetch. So the same picture is stored
 * once, replayed as a URL, and measured nowhere.
 *
 * {@link dropReplayedImages} is the other half: a prompt carries the pictures
 * of the exchange being answered, not every picture the thread ever fetched.
 * Past twenty image blocks in one request Anthropic caps every image at
 * {@link MAX_IMAGE_EDGE_PX} a side and rejects the request otherwise, and a
 * rejection built from stored history is replayed by the next turn too: the
 * chat is dead, not the turn. An older read replays its text with a line
 * saying the picture is one read away, and a re-read is a lookup in the store
 * rather than a second render.
 *
 * A user's own image is not a tool result and is never dropped: a read can be
 * repeated, and what someone attached cannot.
 */
import type { JSONValue, ModelMessage } from "ai";

import type { ToolResultOutput } from "./file-tools";
import { urlModelPart } from "./model-media";

/**
 * What a picture is scaled to when it is stored for a model. Claude
 * downsamples past its own long-edge limit anyway, so a bigger picture buys no
 * detail, and this keeps even a read of many pages inside the cap that applies
 * to it.
 */
export const MAX_IMAGE_EDGE_PX = 2000;

/** How long a URL handed to a provider stays good: long enough for the request
 *  it rides on, and never long enough to be worth storing. */
export const MODEL_URL_TTL_SECONDS = 60 * 5;

/** A file a read hands the model, by reference: a page picture, a picture, a
 *  whole document. `url` is durable and browser-loadable
 *  (`/api/files?source=…`), so the transcript and the file tree render it with
 *  no help. Never bytes, and never a signed URL: those expire. */
export interface ModelFile {
  url: string;
  mediaType: string;
}

/** The durable, browser-loadable form of a stored object. */
export const fileRef = (uri: string) =>
  `/api/files?source=${encodeURIComponent(uri)}`;

/** The storage uri behind a reference, or null when the value isn't one. The
 *  presign route gates on this, so a reference that named someone else's
 *  bucket never becomes a URL. A reference is exactly what {@link fileRef}
 *  makes: one the row cap string-truncated (`... [truncated N chars]`) names
 *  no object, and signing it hands the provider a 404. */
export function refSource(ref: string): string | null {
  const uri = new URLSearchParams(ref.split("?")[1]).get("source");
  return uri && /^(s3|r2):\/\//.test(uri) && fileRef(uri) === ref ? uri : null;
}

/** Everything a read of one file produced: its text layer and its files. */
export interface StoredRead {
  content?: string;
  files: ModelFile[];
}

/** Where reads are kept, keyed by the work that made them (the source's
 *  content hash plus what was asked of it), so a repeat read is a lookup. */
export interface ReadStore {
  /** The read this work key already produced, or null if it hasn't run. */
  storedRead(key: string): Promise<StoredRead | null>;
  /** Publish what a read produced, so the next one is a lookup. */
  storeRead(key: string, read: StoredRead): Promise<StoredRead>;
  /**
   * Put one ALREADY model-readable picture. The caller owns that guarantee: a
   * page is rendered inside the cap and an oversized source is scaled before
   * it gets here, so nothing re-checks downstream.
   */
  storeImage(
    key: string,
    bytes: Blob | Uint8Array,
    mediaType: string,
  ): Promise<ModelFile>;
}

/**
 * A read of something that isn't text, on every host: its text layer if it has
 * one, and the files to look at, by reference. Pages of a PDF, a picture, or a
 * whole document all take this one shape; what a model is shown is decided by
 * each file's type, in {@link readToModelOutput}.
 */
export interface MediaRead extends StoredRead {
  ok: true;
  path: string;
}

/**
 * Project a read for the model: text and misses stay JSON, and each file
 * becomes a part by its type (a picture an image, anything else a file) at
 * whatever URL `resolve` makes of its reference.
 *
 * `resolve` runs at EVERY prompt build, a just-executed read and its hundredth
 * replay alike, which is why a signed URL lives here and never in the stored
 * result: those expire, and the result does not. The api signs against S3,
 * Hearth against R2, bear's worker looks up a batch it signed through the
 * bridge, and a harness hands back a data URL from disk.
 */
export async function readToModelOutput(
  output: unknown,
  resolve: (file: ModelFile) => string | null | Promise<string | null>,
): Promise<ToolResultOutput> {
  const read = output as Partial<MediaRead> | null;
  if (!read?.files) {
    return typeof output === "string"
      ? { type: "text", value: output }
      : { type: "json", value: (output ?? null) as JSONValue };
  }
  const text = read.content
    ? [{ type: "text" as const, text: read.content }]
    : [];
  const parts = await Promise.all(
    read.files.map(async (file) => {
      const url = await resolve(file);
      return url
        ? urlModelPart(file.mediaType, url)
        : {
            type: "text" as const,
            text: `[${read.path} (${file.mediaType}) could not be attached: this deployment cannot issue a URL for it.]`,
          };
    }),
  );
  return { type: "content", value: [...text, ...parts] };
}

const REPLACEMENT = {
  type: "text" as const,
  text: "[the picture from this read is not replayed. Read the file again to look at it.]",
};

/** Anything a provider treats as an image, whatever kind it is filed under:
 *  `image-data`, `image-url`, a `file-data` labeled as an image, or the SDK's
 *  deprecated `media` alias, which is what an MCP image result is minted as. */
function isImage(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const part = item as { type?: unknown; mediaType?: unknown };
  return (
    part.type === "image-url" ||
    (typeof part.mediaType === "string" && part.mediaType.startsWith("image/"))
  );
}

function withoutImages(
  part: Extract<ModelMessage & { role: "tool" }, object>["content"][number],
) {
  if (part.type !== "tool-result" || part.output.type !== "content")
    return part;
  const kept = part.output.value.filter((item) => !isImage(item));
  if (kept.length === part.output.value.length) return part;
  return {
    ...part,
    output: { ...part.output, value: [...kept, REPLACEMENT] },
  };
}

/**
 * The prompt with every tool result older than the current exchange stripped of
 * its pictures. The exchange being answered is everything after the last user
 * message, which is what the model is looking at right now.
 */
export function dropReplayedImages(messages: ModelMessage[]): ModelMessage[] {
  const answering = messages.findLastIndex((m) => m.role === "user");
  return messages.map((message, at) =>
    message.role === "tool" && at < answering
      ? { ...message, content: message.content.map(withoutImages) }
      : message,
  );
}
